import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isCollecting, recordStructuredData } from "@/lib/seoCollector";


const SITE = "https://wtrrestoration.com";

// Human-friendly labels for slugs that don't title-case well
const LABEL_OVERRIDES: Record<string, string> = {
  "lp": "Offers",
  "es": "Español",
  "emergencia": "Emergencia",
  "faq": "FAQ",
  "seo": "SEO",
  "wtr": "WTR",
  "our-work": "Our Work",
  "hoa": "HOA",
  "iicrc": "IICRC",
};

const SMALL_WORDS = new Set(["a", "an", "and", "the", "of", "in", "on", "for", "to", "with", "after", "vs"]);

const toLabel = (slug: string) => {
  if (LABEL_OVERRIDES[slug]) return LABEL_OVERRIDES[slug];
  return slug
    .split("-")
    .map((word, i) => {
      const upper = LABEL_OVERRIDES[word];
      if (upper) return upper;
      if (i > 0 && SMALL_WORDS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const buildBreadcrumb = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      ...segments.map((segment, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name: toLabel(segment),
        item: `${SITE}/${segments.slice(0, index + 1).join("/")}`,
      })),
    ],
  };
};

/**
 * Emits a sitewide BreadcrumbList JSON-LD into <head> for every route,
 * so crawlers understand hierarchy even on pages without visible breadcrumbs.
 */
export const BreadcrumbSchema = () => {
  const { pathname } = useLocation();

  if (isCollecting()) {
    const data = buildBreadcrumb(pathname);
    if (data) recordStructuredData(data);
  }

  useEffect(() => {
    const remove = () => {
      document.querySelector('script[data-breadcrumb-schema]')?.remove();
    };

    remove();

    const data = buildBreadcrumb(pathname);
    if (!data) return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-breadcrumb-schema", "true");
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    return remove;
  }, [pathname]);


  return null;
};
